import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    CheckCircleIcon,
    XCircleIcon,
    UserIcon,
    UsersIcon,
    BuildingOfficeIcon,
    CalendarIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    BriefcaseIcon,
    ClipboardDocumentListIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import {
    CheckCircleIcon as CheckCircleIconSolid,
    XCircleIcon as XCircleIconSolid,
    ClockIcon as ClockIconSolid,
    ExclamationTriangleIcon as ExclamationTriangleIconSolid
} from '@heroicons/react/24/solid';
import { toast } from 'sonner';
import { API_CONFIG } from '../../config/api';
import { useUserEmail } from '../../hooks/useUserEmail';

interface ApprovalData {
    approval: {
        id: number;
        approvalLevel: number;
        approverType: string;
        approverEmail: string;
        status: string;
        tokenExpiresAt: string;
    };
    employee: {
        fullName: string;
        employeeNumber: string;
        designation: string;
        personalEmail: string;
        workEmail?: string;
        employmentDate: string;
        location: string;
        department: string;
    };
    request: {
        id: number;
        requestType: string;
        requiresHardware: boolean;
        remarks?: string;
        dueDate?: string;
        submittedBy: string;
        submittedDate: string;
        requiresSecondApproval: boolean;
    };
    tasks: Array<{
        id: number;
        itemName: string;
        description?: string;
        status: string;
        picId?: string;
        picEmail?: string;
        teamId?: number;
        teamName?: string;
        requiresApproval?: boolean;
        approverEmail?: string;
        approvalStatus?: number;
        rejectionReason?: string;
        rejectionDate?: string;
        category?: string;
    }>;
}

// Component to display user email by resolving userId
function UserEmail({ userId }: { userId: string }) {
    const email = useUserEmail(userId);
    return <span>{email || userId}</span>;
}

export default function ApprovalReview() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ApprovalData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [errorType, setErrorType] = useState<string | null>(null);
    const [errorApprovalId, setErrorApprovalId] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [actionTaken, setActionTaken] = useState<'approved' | 'rejected' | null>(null);
    const [resendEmail, setResendEmail] = useState('');
    const [resendSubmitting, setResendSubmitting] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);

    useEffect(() => {
        const fetchApprovalData = async () => {
            if (!token) {
                setError('No approval token provided');
                setLoading(false);
                return;
            }

            try {
                // Try to extract approval ID from token for resend functionality
                try {
                    const padded = token + '='.repeat((4 - (token.length % 4)) % 4);
                    const outer = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
                    const payload = JSON.parse(atob(outer.Payload));
                    if (payload.ApprovalId) setErrorApprovalId(payload.ApprovalId);
                } catch { /* Token parsing failed, resend won't be available */ }

                const response = await fetch(`${API_CONFIG.baseUrl}/api/approvals/review?token=${encodeURIComponent(token)}`);

                if (!response.ok) {
                    const errorData = await response.json();
                    setErrorType(errorData.error || null);
                    throw new Error(errorData.message || 'Failed to load approval request');
                }

                const result = await response.json();
                setData(result);
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : 'Failed to load approval request';
                setError(message);
            } finally {
                setLoading(false);
            }
        };

        fetchApprovalData();
    }, [token]);

    const handleApprove = async () => {
        if (!window.confirm('Are you sure you want to approve this request?')) {
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/api/approvals/process`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    action: 'approve'
                }),
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || 'Failed to approve request');
            }

            // Show success screen
            setSuccessMessage(result.message || 'Request approved successfully');
            setActionTaken('approved');
        } catch (err: unknown) {
            toast.error(err instanceof Error ? err.message : 'Failed to approve request');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim() || rejectionReason.trim().length < 10) {
            toast.error('Please provide a rejection reason of at least 10 characters');
            return;
        }

        if (!window.confirm('Are you sure you want to reject this request?')) {
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/api/approvals/process`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    action: 'reject',
                    rejectionReason: rejectionReason.trim()
                }),
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || 'Failed to reject request');
            }

            // Show success screen
            setSuccessMessage(result.message || 'Request rejected successfully');
            setActionTaken('rejected');
        } catch (err: unknown) {
            toast.error(err instanceof Error ? err.message : 'Failed to reject request');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRequestNewLink = async () => {
        if (!resendEmail.trim() || !errorApprovalId) return;

        setResendSubmitting(true);
        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/api/approvals/request-new-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    approvalId: errorApprovalId,
                    approverEmail: resendEmail.trim()
                }),
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || 'Failed to request new link');
            }

            setResendSuccess(true);
        } catch (err: unknown) {
            toast.error(err instanceof Error ? err.message : 'Failed to request new link');
        } finally {
            setResendSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <p className="mt-4 text-gray-600">Loading approval request...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className={`min-h-screen bg-gradient-to-br ${errorType === 'Already Processed' ? 'from-amber-50 to-yellow-100' : 'from-red-50 to-pink-100'} flex items-center justify-center p-4`}>
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    {errorType === 'Already Processed' ? (
                        <CheckCircleIcon className="h-16 w-16 text-amber-500 mx-auto mb-4" />
                    ) : (
                        <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    )}
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        {errorType === 'Already Processed' ? 'Already Processed' : 'Unable to Process Request'}
                    </h1>
                    <p className="text-gray-600 mb-6">{error || 'Approval request not found'}</p>

                    {errorApprovalId && !resendSuccess && errorType !== 'Already Processed' && (
                        <div className="border-t border-gray-200 pt-6 mt-2">
                            <p className="text-sm text-gray-600 mb-3">
                                If your approval link has expired or is no longer valid, enter your email below to receive a new one.
                            </p>
                            <div className="flex flex-col gap-3">
                                <input
                                    type="email"
                                    value={resendEmail}
                                    onChange={(e) => setResendEmail(e.target.value)}
                                    placeholder="Enter your email address"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 text-sm"
                                />
                                <button
                                    onClick={handleRequestNewLink}
                                    disabled={resendSubmitting || !resendEmail.trim()}
                                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors flex items-center justify-center"
                                >
                                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                                    {resendSubmitting ? 'Sending...' : 'Request New Approval Link'}
                                </button>
                            </div>
                        </div>
                    )}

                    {resendSuccess && (
                        <div className="border-t border-gray-200 pt-6 mt-2">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <CheckCircleIcon className="h-8 w-8 text-green-500 mx-auto mb-2" />
                                <p className="text-sm text-green-800 font-medium">
                                    A new approval link has been sent to your email.
                                </p>
                                <p className="text-xs text-green-600 mt-1">
                                    Please check your inbox and use the new link to review the request.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Show success confirmation screen
    if (successMessage && actionTaken) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    {actionTaken === 'approved' ? (
                        <>
                            <CheckCircleIcon className="h-20 w-20 text-green-500 mx-auto mb-4" />
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">Approved!</h1>
                        </>
                    ) : (
                        <>
                            <XCircleIcon className="h-20 w-20 text-red-500 mx-auto mb-4" />
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">Rejected</h1>
                        </>
                    )}
                    <p className="text-gray-700 text-lg mb-4">{successMessage}</p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                        <p className="text-sm text-blue-900">
                            Your decision has been recorded and the relevant parties have been notified.
                            You may now close this page.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const approvalLevelText = data.approval.approvalLevel === 1 ? 'First' : 'Second';
    const approverTitle = data.approval.approverType === 'ImmediateSuperior'
        ? 'Immediate Superior'
        : 'Second Level Approver';

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-6">
                    <div className="text-center">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">📋 Approval Request Review</h1>
                        <span className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">
                            {approverTitle} - {approvalLevelText} Approval
                        </span>
                    </div>
                </div>

                {/* Employee Information */}
                <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                        <UserIcon className="h-6 w-6 mr-2 text-blue-600" />
                        Employee Information
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start">
                            <UserIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
                                <p className="text-base font-semibold text-gray-900">{data.employee.fullName}</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <BriefcaseIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Position</label>
                                <p className="text-base font-semibold text-gray-900">{data.employee.designation}</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Department</label>
                                <p className="text-base font-semibold text-gray-900">{data.employee.department}</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Location</label>
                                <p className="text-base font-semibold text-gray-900">{data.employee.location}</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                                <p className="text-base font-semibold text-gray-900">{data.employee.workEmail || data.employee.personalEmail}</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <CalendarIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Employment Date</label>
                                <p className="text-base font-semibold text-gray-900">
                                    {new Date(data.employee.employmentDate + 'Z').toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Request Details */}
                <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">📄 Request Details</h2>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Request Type:</span>
                            <span className="font-semibold text-gray-900">{data.request.requestType}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Submitted By:</span>
                            <span className="font-semibold text-gray-900">
                                <UserEmail userId={data.request.submittedBy} />
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Submitted Date:</span>
                            <span className="font-semibold text-gray-900">
                                {new Date(data.request.submittedDate + 'Z').toLocaleString()}
                            </span>
                        </div>
                        {data.request.remarks && (
                            <div>
                                <span className="text-gray-600 block mb-1">Remarks:</span>
                                <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{data.request.remarks}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Tasks */}
                {data.tasks && data.tasks.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <ClipboardDocumentListIcon className="h-6 w-6 text-blue-600" />
                            Tasks to be Assigned ({data.tasks.length})
                        </h2>
                        <div className="space-y-6">
                            {(() => {
                                // Group tasks by category
                                const tasksByCategory: Record<string, typeof data.tasks> = {};
                                data.tasks.forEach(task => {
                                    const category = task.category || 'Uncategorized';
                                    if (!tasksByCategory[category]) {
                                        tasksByCategory[category] = [];
                                    }
                                    tasksByCategory[category].push(task);
                                });

                                // Sort categories alphabetically
                                const sortedCategories = Object.keys(tasksByCategory).sort();

                                return sortedCategories.map(category => (
                                    <div key={category} className="space-y-3">
                                        <h3 className="text-lg font-semibold text-blue-600 border-b border-blue-200 pb-2">
                                            {category}
                                        </h3>
                                        {tasksByCategory[category].map((task, index) => (
                                            <div key={task.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                                <p className="font-medium text-gray-900">{task.itemName}</p>
                                                {task.description && (
                                                    <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                                                )}

                                                {/* Assignment Information */}
                                                <div className="mt-3 space-y-2">
                                                    {task.teamName ? (
                                                        <div className="flex items-center text-sm">
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                <UsersIcon className="h-3 w-3" />
                                                                Team: {task.teamName}
                                                            </span>
                                                        </div>
                                                    ) : task.picEmail ? (
                                                        <div className="flex items-center text-sm">
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                <UserIcon className="h-3 w-3" />
                                                                Person: {task.picEmail}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center text-sm">
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                                <ExclamationTriangleIconSolid className="h-3 w-3" />
                                                                Not assigned
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Approval Information */}
                                                    {task.requiresApproval && (
                                                        <div className="flex items-center text-sm">
                                                            {task.approvalStatus === 2 ? (
                                                                <div className="space-y-1">
                                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                        <XCircleIconSolid className="h-3.5 w-3.5" />
                                                                        Rejected by {task.approverEmail}
                                                                    </span>
                                                                    {task.rejectionReason && (
                                                                        <div className="text-xs text-red-700 bg-red-50 p-2 rounded border border-red-200">
                                                                            <span className="font-semibold">Reason: </span>
                                                                            {task.rejectionReason}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : task.approvalStatus === 1 ? (
                                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                    <CheckCircleIconSolid className="h-3.5 w-3.5" />
                                                                    Approved by {task.approverEmail}
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                                                    <ClockIconSolid className="h-3.5 w-3.5" />
                                                                    Pending Approval
                                                                    {task.approverEmail && ` from ${task.approverEmail}`}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>
                )}

                {/* Warning for Two-Level Approval */}
                {data.request.requiresSecondApproval && data.approval.approvalLevel === 1 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 mb-6">
                        <div className="flex items-start">
                            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-yellow-900 mb-1">Two-Level Approval Required</h3>
                                <p className="text-yellow-800 text-sm">
                                    This request requires two-level approval. After your approval, it will be forwarded to the Second Level Approver for final approval.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">🎯 Action Required</h2>
                    <p className="text-gray-600 mb-6">Please review and confirm your choice:</p>

                    {!showRejectForm ? (
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={handleApprove}
                                disabled={submitting}
                                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors flex items-center justify-center"
                            >
                                <CheckCircleIcon className="h-5 w-5 mr-2" />
                                {submitting ? 'Processing...' : 'Approve Request'}
                            </button>
                            <button
                                onClick={() => setShowRejectForm(true)}
                                disabled={submitting}
                                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors flex items-center justify-center"
                            >
                                <XCircleIcon className="h-5 w-5 mr-2" />
                                Reject Request
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
                                    Rejection Reason *
                                </label>
                                <textarea
                                    id="rejectionReason"
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    placeholder="Please explain why you are rejecting this request..."
                                    rows={4}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-900"
                                    minLength={10}
                                    maxLength={1000}
                                    required
                                />
                                <p className="text-sm text-gray-500 mt-1">
                                    Minimum 10 characters required
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={handleReject}
                                    disabled={submitting || rejectionReason.trim().length < 10}
                                    className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
                                >
                                    {submitting ? 'Submitting...' : 'Submit Rejection'}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowRejectForm(false);
                                        setRejectionReason('');
                                    }}
                                    disabled={submitting}
                                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
